import React, { useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Checkbox,
  Flex,
  FormControl,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  addWifiDetails,
  getSavedWifiDetails,
} from '../../features/account/settings/wifi-settings/wifi-settings-helpers';
import { selectSite } from '../../features/site/siteSlice';
import { PasswordInput } from './PasswordInput';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (password: string) => void;
  ssid: string;
};

type State = {
  passwordInput: string;
  isUsingPasswordFromStorage: boolean;
  shouldSaveWifiDetails: boolean;
};

const INITIAL_STATE: State = {
  passwordInput: '',
  isUsingPasswordFromStorage: false,
  shouldSaveWifiDetails: false,
};

export function WiFiPasswordDialog({ isOpen, onClose, onConfirm, ssid }: Props) {
  const { site_id: siteId } = useSelector(selectSite);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const buttonBorderColor = useColorModeValue('customGrey.400', 'customGrey.600');
  const backgroundColor = useColorModeValue('customGrey.100', 'customGrey.800');
  const { t } = useTranslation();
  const [state, setState] = useState<State>(INITIAL_STATE);

  const { passwordInput, shouldSaveWifiDetails, isUsingPasswordFromStorage } = state;

  useEffect(() => {
    async function fetchPasswordForSSID() {
      const savedWifiDetails = await getSavedWifiDetails(siteId);
      const savedWifiDetailsForSSID = savedWifiDetails.filter((wifi) => wifi.ssid === ssid);
      if (savedWifiDetailsForSSID.length) {
        setState((p) => ({
          ...p,
          passwordInput: savedWifiDetailsForSSID[savedWifiDetailsForSSID.length - 1].password,
          isUsingPasswordFromStorage: true,
          shouldSaveWifiDetails: false,
        }));
      }
    }

    fetchPasswordForSSID();
  }, [ssid, siteId]);

  async function onClickConfirm() {
    try {
      if (!isUsingPasswordFromStorage && shouldSaveWifiDetails) {
        // Save the inputted wifi pw if the user wants to save them
        await addWifiDetails(siteId, ssid, passwordInput, false);
        setState((p) => ({
          ...p,
          isUsingPasswordFromStorage: true,
          shouldSaveWifiDetails: false,
        }));
      }
    } catch (e) {
      /* istanbul ignore next -- @preserve */
      console.error('Error saving wifi details:', e);
    }

    onConfirm(passwordInput);
    onClose();
  }

  async function onChangePassword(password: string) {
    setState((p) => ({
      ...p,
      passwordInput: password,
      isUsingPasswordFromStorage: false,
    }));
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef as React.RefObject<HTMLButtonElement>}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent top={'200px'} bgColor={backgroundColor} mx={8} rounded={16} mt={0} overflowX="hidden">
          <AlertDialogBody my={4} textAlign="center" data-testid="wifi-password-dialog">
            {t('Set Up Hardware.enter wifi pw for ssid', { ssid })}
            <FormControl mt={3}>
              <PasswordInput
                onChange={onChangePassword}
                value={passwordInput}
                backgroundColor={backgroundColor}
                borderColor={'#9FA0A4'}
              />
            </FormControl>
            <Checkbox
              defaultChecked={shouldSaveWifiDetails}
              isDisabled={isUsingPasswordFromStorage}
              onChange={() => setState((p) => ({ ...p, shouldSaveWifiDetails: !shouldSaveWifiDetails }))}
              mt={2}
              data-testid="save-wifi-checkbox"
              justifyContent="flex-start"
              w="100%"
            >
              {t('Devices.save wifi')}
            </Checkbox>
          </AlertDialogBody>

          <Flex w="100%" justifyContent={'space-around'}>
            <Button
              w="100%"
              rounded={0}
              py={6}
              fontWeight="normal"
              borderRight="1px solid"
              borderTop="1px solid"
              borderColor={buttonBorderColor}
              variant="ghost"
              onClick={onClose}
              ref={cancelRef}
              data-testid="cancel-button"
            >
              {t('Common.cancel')}
            </Button>

            <Button
              color="#3DCD58"
              w="100%"
              rounded={0}
              py={6}
              variant="ghost"
              borderTop="1px solid"
              borderColor={buttonBorderColor}
              onClick={onClickConfirm}
              data-testid="confirm-button"
            >
              OK
            </Button>
          </Flex>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
